import { useState, useMemo, useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
    AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ComposedChart
} from "recharts";
import {
    generateDailyVisitors, aggregateMonthly, aggregateWeekly,
    getTrafficSources, getDeviceBreakdown, getBrowserBreakdown,
    getGeographicData, getTopPages, getHourlyTraffic,
    getRealtimeUsers, getConversionFunnel, getKPISummary
} from "./mockData";
import { fmt, fmtDuration, CustomTooltip, CHART_COLORS } from "./analyticsHelpers";
import styles from "./Analytics.module.css";

const { BLUE, TEAL, GREEN, CORAL } = CHART_COLORS;

// ============================================
// MAIN ANALYTICS DASHBOARD
// ============================================
export const Analytics = () => {
    const navigate = useNavigate();
    const [period, setPeriod] = useState("3m");
    const [calendarMonth, setCalendarMonth] = useState(dayjs().startOf("month"));
    const [hoveredDay, setHoveredDay] = useState(null);
    const [realtimeCount, setRealtimeCount] = useState(getRealtimeUsers());

    // Simulate realtime updates
    useEffect(() => {
        const interval = setInterval(() => setRealtimeCount(getRealtimeUsers()), 5000);
        return () => clearInterval(interval);
    }, []);

    // ===================== DATA =====================
    const monthsBack = period === "1m" ? 2 : period === "3m" ? 4 : 7;
    const dailyData = useMemo(() => generateDailyVisitors(monthsBack), [monthsBack]);
    const monthlyData = useMemo(() => aggregateMonthly(dailyData), [dailyData]);
    const weeklyData = useMemo(() => aggregateWeekly(dailyData), [dailyData]);
    const kpi = useMemo(() => getKPISummary(monthlyData), [monthlyData]);
    const trafficSources = useMemo(() => getTrafficSources(), []);
    const deviceData = useMemo(() => getDeviceBreakdown(), []);
    const browserData = useMemo(() => getBrowserBreakdown(), []);
    const geoData = useMemo(() => getGeographicData(), []);
    const topPages = useMemo(() => getTopPages(), []);
    const hourlyData = useMemo(() => getHourlyTraffic(), []);
    const funnelData = useMemo(() => getConversionFunnel(), []);

    // ===================== CALENDAR =====================
    const calendarDays = useMemo(() => {
        const firstDay = calendarMonth.startOf("month");
        const daysInMonth = calendarMonth.daysInMonth();
        const startPad = firstDay.day();
        const days = [];

        for (let i = 0; i < startPad; i++) days.push(null);

        for (let d = 1; d <= daysInMonth; d++) {
            const dateStr = calendarMonth.date(d).format("YYYY-MM-DD");
            const dayData = dailyData.find(dd => dd.date === dateStr);
            days.push({
                day: d,
                date: dateStr,
                visitors: dayData?.visitors || 0,
                pageViews: dayData?.pageViews || 0,
                uniqueVisitors: dayData?.uniqueVisitors || 0,
                bounceRate: dayData?.bounceRate || 0,
                isToday: dateStr === dayjs().format("YYYY-MM-DD"),
            });
        }
        return days;
    }, [calendarMonth, dailyData]);

    const maxVisitors = useMemo(() => Math.max(...calendarDays.filter(Boolean).map(d => d.visitors), 1), [calendarDays]);

    const getHeatLevel = useCallback((visitors) => {
        const ratio = visitors / maxVisitors;
        if (ratio === 0) return 0;
        if (ratio < 0.2) return 1;
        if (ratio < 0.4) return 2;
        if (ratio < 0.6) return 3;
        if (ratio < 0.8) return 4;
        return 5;
    }, [maxVisitors]);

    const navigateMonth = (dir) => {
        setCalendarMonth(prev => dir > 0 ? prev.add(1, "month") : prev.subtract(1, "month"));
    };

    const handleDayClick = useCallback((day) => {
        if (!day) return;
        navigate(`/admin/analytics/day/${day.date}`);
    }, [navigate]);

    return (
        <div className={styles.container}>
            {/* ===== HEADER ===== */}
            <header className={styles.header}>
                <div className={styles.headerTop}>
                    <Link to="/admin" className={styles.backBtn}>
                        <span className="material-symbols-outlined">arrow_back</span> Admin
                    </Link>
                    <div className={styles.headerTitle}>
                        <span className={styles.headerIcon}>📊</span>
                        <div>
                            <h1>Analytics Dashboard</h1>
                            <p>Website traffic & visitor insights</p>
                        </div>
                    </div>
                    <div className={styles.headerActions}>
                        <div className={styles.realtimeBadge}>
                            <span className={styles.realtimeDot} />
                            <span>{realtimeCount} active now</span>
                        </div>
                        {["1m", "3m", "6m"].map(p => (
                            <button key={p} className={`${styles.periodBtn} ${period === p ? styles.periodBtnActive : ""}`} onClick={() => setPeriod(p)}>
                                {p === "1m" ? "1 Month" : p === "3m" ? "3 Months" : "6 Months"}
                            </button>
                        ))}
                    </div>
                </div>
            </header>

            <main className={styles.main}>
                {/* ===== KPI CARDS ===== */}
                {kpi && (
                    <div className={styles.kpiGrid}>
                        {[
                            { label: "Total Visitors", ...kpi.totalVisitors, format: fmt },
                            { label: "Page Views", ...kpi.pageViews, format: fmt },
                            { label: "Unique Visitors", ...kpi.uniqueVisitors, format: fmt },
                            { label: "Bounce Rate", ...kpi.bounceRate, format: v => `${v}%`, invertColor: true },
                            { label: "Avg Session", ...kpi.avgSessionDuration, format: fmtDuration },
                            { label: "New Users", ...kpi.newUsers, format: fmt },
                        ].map((k, i) => (
                            <div key={i} className={styles.kpiCard}>
                                <div className={styles.kpiLabel}>{k.label}</div>
                                <div className={styles.kpiValue}>{k.format(k.value)}</div>
                                <span className={`${styles.kpiChange} ${
                                    k.invertColor
                                        ? (k.change <= 0 ? styles.kpiUp : styles.kpiDown)
                                        : (k.change >= 0 ? styles.kpiUp : styles.kpiDown)
                                }`}>
                                    {k.change >= 0 ? "↑" : "↓"} {Math.abs(k.change)}%
                                </span>
                                <div className={styles.kpiPrev}>vs. {k.format(k.prev)} last month</div>
                            </div>
                        ))}
                    </div>
                )}

                {/* ===== CALENDAR HEATMAP ===== */}
                <div className={styles.calendarSection}>
                    <div className={styles.calendarHeader}>
                        <div className={styles.chartTitle}>
                            📅 Daily Visitor Heatmap
                            <span className={styles.chartSubtitle}>Click a day to see full report</span>
                        </div>
                        <div className={styles.calendarNav}>
                            <button className={styles.calendarNavBtn} onClick={() => navigateMonth(-1)}>
                                <span className="material-symbols-outlined">chevron_left</span>
                            </button>
                            <span className={styles.calendarMonthLabel}>{calendarMonth.format("MMMM YYYY")}</span>
                            <button className={styles.calendarNavBtn} onClick={() => navigateMonth(1)} disabled={calendarMonth.add(1, "month").isAfter(dayjs())}>
                                <span className="material-symbols-outlined">chevron_right</span>
                            </button>
                        </div>
                        <div className={styles.calendarLegend}>
                            <span className={styles.legendItem}><span className={styles.legendDot} style={{ background: "#EEF2FF" }} /> Low</span>
                            <span className={styles.legendItem}><span className={styles.legendDot} style={{ background: "#818CF8" }} /> Medium</span>
                            <span className={styles.legendItem}><span className={styles.legendDot} style={{ background: "#4338CA" }} /> High</span>
                        </div>
                    </div>
                    <div className={styles.calendarGrid}>
                        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
                            <div key={d} className={styles.calendarDayHeader}>{d}</div>
                        ))}
                        {calendarDays.map((day, idx) => (
                            <div
                                key={idx}
                                className={`${styles.calendarDay} ${!day ? styles.calendarDayEmpty : styles[`heatLevel${getHeatLevel(day?.visitors || 0)}`]} ${day?.isToday ? styles.calendarDayToday : ""}`}
                                onMouseEnter={() => day && setHoveredDay(idx)}
                                onMouseLeave={() => setHoveredDay(null)}
                                onClick={() => handleDayClick(day)}
                            >
                                {day && (
                                    <>
                                        <span className={styles.calendarDayNumber}>{day.day}</span>
                                        <span className={styles.calendarDayVisitors}>{day.visitors}</span>
                                        {hoveredDay === idx && (
                                            <div className={styles.calendarTooltip}>
                                                <div style={{ fontWeight: 700, marginBottom: 4 }}>{dayjs(day.date).format("ddd, MMM D")}</div>
                                                <div className={styles.tooltipRow}><span className={styles.tooltipLabel}>Visitors</span> <span className={styles.tooltipValue}>{fmt(day.visitors)}</span></div>
                                                <div className={styles.tooltipRow}><span className={styles.tooltipLabel}>Page Views</span> <span className={styles.tooltipValue}>{fmt(day.pageViews)}</span></div>
                                                <div className={styles.tooltipRow}><span className={styles.tooltipLabel}>Unique</span> <span className={styles.tooltipValue}>{fmt(day.uniqueVisitors)}</span></div>
                                                <div className={styles.tooltipRow}><span className={styles.tooltipLabel}>Bounce</span> <span className={styles.tooltipValue}>{day.bounceRate}%</span></div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* ===== VISITORS AREA CHART + MONTHLY BAR ===== */}
                <div className={styles.chartsRow}>
                    <div className={styles.chartCard}>
                        <div className={styles.chartHeader}>
                            <div>
                                <div className={styles.chartTitle}>📈 Visitor Trend</div>
                                <div className={styles.chartSubtitle}>Daily visitors over time</div>
                            </div>
                        </div>
                        <ResponsiveContainer width="100%" height={280}>
                            <AreaChart data={dailyData.slice(-60)} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="gradBlue" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={BLUE} stopOpacity={0.3} />
                                        <stop offset="100%" stopColor={BLUE} stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                                <XAxis dataKey="date" tickFormatter={v => dayjs(v).format("MMM D")} tick={{ fontSize: 11, fill: "#8C95A4" }} interval="preserveStartEnd" />
                                <YAxis tick={{ fontSize: 11, fill: "#8C95A4" }} />
                                <Tooltip content={<CustomTooltip />} />
                                <Area type="monotone" dataKey="visitors" name="Visitors" stroke={BLUE} fill="url(#gradBlue)" strokeWidth={2.5} dot={false} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>

                    <div className={styles.chartCard}>
                        <div className={styles.chartHeader}>
                            <div>
                                <div className={styles.chartTitle}>📊 Monthly Comparison</div>
                                <div className={styles.chartSubtitle}>Visitors & page views per month</div>
                            </div>
                        </div>
                        <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={monthlyData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                                <XAxis dataKey="shortLabel" tick={{ fontSize: 12, fill: "#8C95A4" }} />
                                <YAxis tick={{ fontSize: 11, fill: "#8C95A4" }} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Bar dataKey="visitors" name="Visitors" fill={BLUE} radius={[6, 6, 0, 0]} barSize={32} />
                                <Bar dataKey="pageViews" name="Page Views" fill={TEAL} radius={[6, 6, 0, 0]} barSize={32} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* ===== NEW VS RETURNING + HOURLY ===== */}
                <div className={styles.chartsRow}>
                    <div className={styles.chartCard}>
                        <div className={styles.chartHeader}>
                            <div>
                                <div className={styles.chartTitle}>👤 New vs Returning Users</div>
                                <div className={styles.chartSubtitle}>Monthly breakdown</div>
                            </div>
                        </div>
                        <ResponsiveContainer width="100%" height={280}>
                            <ComposedChart data={monthlyData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                                <XAxis dataKey="shortLabel" tick={{ fontSize: 12, fill: "#8C95A4" }} />
                                <YAxis tick={{ fontSize: 11, fill: "#8C95A4" }} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Bar dataKey="newUsers" name="New Users" stackId="a" fill={GREEN} radius={[0, 0, 0, 0]} barSize={40} />
                                <Bar dataKey="returningUsers" name="Returning" stackId="a" fill={BLUE} radius={[6, 6, 0, 0]} barSize={40} />
                                <Line type="monotone" dataKey="visitors" name="Total" stroke={CORAL} strokeWidth={2} dot={{ r: 4 }} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>

                    <div className={styles.chartCard}>
                        <div className={styles.chartHeader}>
                            <div>
                                <div className={styles.chartTitle}>🕐 Today&apos;s Hourly Traffic</div>
                                <div className={styles.chartSubtitle}>Visitor distribution by hour</div>
                            </div>
                            <span className={styles.chartBadge}>Live</span>
                        </div>
                        <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={hourlyData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#8C95A4" }} interval={2} />
                                <YAxis tick={{ fontSize: 11, fill: "#8C95A4" }} />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="visitors" name="Visitors" fill={TEAL} radius={[4, 4, 0, 0]} barSize={18} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* ===== PIES: Traffic Sources, Devices, Browsers ===== */}
                <div className={styles.chartsRow3}>
                    <div className={styles.chartCard}>
                        <div className={styles.chartHeader}>
                            <div className={styles.chartTitle}>🌐 Traffic Sources</div>
                        </div>
                        <div className={styles.pieContainer}>
                            <ResponsiveContainer width={160} height={160}>
                                <PieChart>
                                    <Pie data={trafficSources} dataKey="value" cx="50%" cy="50%" innerRadius={40} outerRadius={72} paddingAngle={2} strokeWidth={0}>
                                        {trafficSources.map((s, i) => <Cell key={i} fill={s.color} />)}
                                    </Pie>
                                    <Tooltip formatter={(val) => `${val}%`} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className={styles.pieLegend}>
                                {trafficSources.map((s, i) => (
                                    <div key={i} className={styles.pieLegendItem}>
                                        <span className={styles.pieLegendDot} style={{ background: s.color }} />
                                        <span className={styles.pieLegendName}>{s.name}</span>
                                        <span className={styles.pieLegendValue}>{s.value}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className={styles.chartCard}>
                        <div className={styles.chartHeader}>
                            <div className={styles.chartTitle}>📱 Device Breakdown</div>
                        </div>
                        <div className={styles.pieContainer}>
                            <ResponsiveContainer width={160} height={160}>
                                <PieChart>
                                    <Pie data={deviceData} dataKey="value" cx="50%" cy="50%" innerRadius={40} outerRadius={72} paddingAngle={2} strokeWidth={0}>
                                        {deviceData.map((s, i) => <Cell key={i} fill={s.color} />)}
                                    </Pie>
                                    <Tooltip formatter={(val) => `${val}%`} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className={styles.pieLegend}>
                                {deviceData.map((s, i) => (
                                    <div key={i} className={styles.pieLegendItem}>
                                        <span className={styles.pieLegendDot} style={{ background: s.color }} />
                                        <span className={styles.pieLegendName}>{s.name}</span>
                                        <span className={styles.pieLegendValue}>{s.value}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className={styles.chartCard}>
                        <div className={styles.chartHeader}>
                            <div className={styles.chartTitle}>🔍 Browser Usage</div>
                        </div>
                        <div className={styles.pieContainer}>
                            <ResponsiveContainer width={160} height={160}>
                                <PieChart>
                                    <Pie data={browserData} dataKey="value" cx="50%" cy="50%" innerRadius={40} outerRadius={72} paddingAngle={2} strokeWidth={0}>
                                        {browserData.map((s, i) => <Cell key={i} fill={s.color} />)}
                                    </Pie>
                                    <Tooltip formatter={(val) => `${val}%`} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className={styles.pieLegend}>
                                {browserData.map((s, i) => (
                                    <div key={i} className={styles.pieLegendItem}>
                                        <span className={styles.pieLegendDot} style={{ background: s.color }} />
                                        <span className={styles.pieLegendName}>{s.name}</span>
                                        <span className={styles.pieLegendValue}>{s.value}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* ===== WEEKLY TREND LINE ===== */}
                <div className={styles.chartsRowFull}>
                    <div className={styles.chartCard}>
                        <div className={styles.chartHeader}>
                            <div>
                                <div className={styles.chartTitle}>📉 Weekly Visitor Trend</div>
                                <div className={styles.chartSubtitle}>Visitors & unique visitors by week</div>
                            </div>
                        </div>
                        <ResponsiveContainer width="100%" height={260}>
                            <LineChart data={weeklyData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#8C95A4" }} />
                                <YAxis tick={{ fontSize: 11, fill: "#8C95A4" }} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Line type="monotone" dataKey="visitors" name="Total Visitors" stroke={BLUE} strokeWidth={2.5} dot={{ r: 3, fill: BLUE }} activeDot={{ r: 5 }} />
                                <Line type="monotone" dataKey="uniqueVisitors" name="Unique Visitors" stroke={GREEN} strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3, fill: GREEN }} />
                                <Line type="monotone" dataKey="pageViews" name="Page Views" stroke={TEAL} strokeWidth={2} dot={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* ===== CONVERSION FUNNEL + GEOGRAPHY ===== */}
                <div className={styles.chartsRow}>
                    <div className={styles.chartCard}>
                        <div className={styles.chartHeader}>
                            <div>
                                <div className={styles.chartTitle}>🎯 Conversion Funnel</div>
                                <div className={styles.chartSubtitle}>User journey from visit to action</div>
                            </div>
                        </div>
                        <div style={{ padding: "12px 0" }}>
                            {funnelData.map((step, i) => {
                                const widthPct = (step.value / funnelData[0].value) * 100;
                                return (
                                    <div key={i} className={styles.funnelStep}>
                                        <span className={styles.funnelLabel}>{step.step}</span>
                                        <div className={styles.funnelBarWrap}>
                                            <div className={styles.funnelBar} style={{ width: `${widthPct}%`, background: step.color }}>
                                                {widthPct > 15 && <span className={styles.funnelBarText}>{Math.round(widthPct)}%</span>}
                                            </div>
                                        </div>
                                        <span className={styles.funnelValue}>{fmt(step.value)}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className={styles.chartCard}>
                        <div className={styles.chartHeader}>
                            <div>
                                <div className={styles.chartTitle}>🌍 Top Countries</div>
                                <div className={styles.chartSubtitle}>Where your visitors come from</div>
                            </div>
                        </div>
                        <div>
                            {geoData.map((g, i) => (
                                <div key={i} className={styles.geoRow}>
                                    <span className={styles.geoFlag}>{g.flag}</span>
                                    <div className={styles.geoInfo}>
                                        <div className={styles.geoCountry}>{g.country}</div>
                                        <div className={styles.geoBar}>
                                            <div className={styles.geoBarFill} style={{ width: `${g.percent * 3}%` }} />
                                        </div>
                                    </div>
                                    <span className={styles.geoVisitors}>{fmt(g.visitors)}</span>
                                    <span className={styles.geoPercent}>{g.percent}%</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ===== TOP PAGES TABLE ===== */}
                <div className={styles.chartsRowFull}>
                    <div className={styles.chartCard}>
                        <div className={styles.chartHeader}>
                            <div>
                                <div className={styles.chartTitle}>📄 Top Pages</div>
                                <div className={styles.chartSubtitle}>Most visited pages this period</div>
                            </div>
                        </div>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Page</th>
                                    <th>Views</th>
                                    <th>Unique</th>
                                    <th>Avg Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topPages.map((p, i) => (
                                    <tr key={i}>
                                        <td>
                                            {p.title}
                                            <span className={styles.pageUrl}>{p.page}</span>
                                        </td>
                                        <td>{fmt(p.views)}</td>
                                        <td>{fmt(p.unique)}</td>
                                        <td>{p.avgTime}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* ===== BOUNCE RATE TREND ===== */}
                <div className={styles.chartsRowFull}>
                    <div className={styles.chartCard}>
                        <div className={styles.chartHeader}>
                            <div>
                                <div className={styles.chartTitle}>🏀 Bounce Rate & Session Duration</div>
                                <div className={styles.chartSubtitle}>Monthly averages</div>
                            </div>
                        </div>
                        <ResponsiveContainer width="100%" height={260}>
                            <ComposedChart data={monthlyData} margin={{ top: 5, right: 20, left: -10, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                                <XAxis dataKey="shortLabel" tick={{ fontSize: 12, fill: "#8C95A4" }} />
                                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#8C95A4" }} unit="%" />
                                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#8C95A4" }} unit="s" />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Bar yAxisId="right" dataKey="avgSessionDuration" name="Avg Session (s)" fill={TEAL} radius={[6, 6, 0, 0]} barSize={28} fillOpacity={0.6} />
                                <Line yAxisId="left" type="monotone" dataKey="avgBounceRate" name="Bounce Rate %" stroke={CORAL} strokeWidth={2.5} dot={{ r: 4, fill: CORAL }} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </main>
        </div>
    );
};
